// Half-day leave — the decisions the apply form has to get right.
//
// Extracted from Leaves.tsx so they can be tested without rendering a
// 2000-line page. They are guards, not authority: the server re-validates the
// session against `leave.half_day`, re-derives totalDays from it, and rejects a
// multi-date half day regardless of what the form sends. These exist so the
// employee gets told on the spot instead of via a failed request.

import type { HalfDayRanges } from "../api/leaves";

export interface HalfDayConfig {
  enabled: boolean;
  sessions: string[];
  /** The halves as clock ranges, when the branch measures them by the clock. */
  ranges?: HalfDayRanges;
}

export const EMPTY_HALF_DAY: HalfDayConfig = { enabled: false, sessions: [] };

/** Is the half-day control offered at all?
 *
 * Both halves disabled is a real configuration and means "not offered" — a
 * branch that turns the rule on but permits no session has said no.
 */
export function canOfferHalfDay(config: HalfDayConfig | undefined): boolean {
  return Boolean(config?.enabled) && (config?.sessions?.length ?? 0) > 0;
}

/** The session to preselect when the box is ticked.
 *
 * One permitted half means there is nothing to choose, so choosing for them is
 * kinder than presenting a radio group of one.
 */
export function defaultSession(config: HalfDayConfig | undefined): string {
  const sessions = config?.sessions ?? [];
  return sessions.length === 1 ? sessions[0] : "";
}

/** "09:30 – 13:30", or null when the branch has not defined that half by
 *  the clock. A half with only one edge set reads as "… – 13:30". */
export function rangeText(config: HalfDayConfig | undefined, session: string): string | null {
  const span = config?.ranges?.[session as "first_half" | "second_half"];
  if (!span || (!span.from && !span.to)) return null;
  return `${span.from || "…"} – ${span.to || "…"}`;
}

export function sessionLabel(session: string, config?: HalfDayConfig): string {
  const base =
    session === "first_half" ? "First half (morning off)"
    : session === "second_half" ? "Second half (afternoon off)"
    : session;
  const span = rangeText(config, session);
  return span ? `${base} · ${span}` : base;
}

/** The half the employee still has to work, with its hours when known — the
 *  same range payroll scores that half against. */
export function expectedAtWorkText(config: HalfDayConfig | undefined, session: string): string {
  const other = session === "first_half" ? "second_half" : session === "second_half" ? "first_half" : "";
  const span = other ? rangeText(config, other) : null;
  return span
    ? `You're expected at work for the other half, ${span}.`
    : "You're expected at work for the other half.";
}

export type HalfDayCheck =
  | { ok: true; totalDays: number; session?: string }
  | { ok: false; title: string; description: string };

/** Validate the half-day part of an application and give back its day count.
 *
 * `spanDays` is the inclusive length of the chosen range (1 for a single date).
 */
export function checkHalfDay(args: {
  active: boolean;
  session: string;
  spanDays: number;
}): HalfDayCheck {
  const { active, session, spanDays } = args;

  if (!active) {
    return { ok: true, totalDays: spanDays };
  }
  // Half a day is half of ONE day. Across a range it would mean "half of each
  // of these days", which is a different policy and one payroll cannot score.
  if (spanDays !== 1) {
    return {
      ok: false,
      title: "Half day needs a single date",
      description: "Set the same start and end date, or turn off half day.",
    };
  }
  if (!session) {
    return {
      ok: false,
      title: "Which half?",
      description: "Pick the half of the day you're taking off.",
    };
  }
  return { ok: true, totalDays: 0.5, session };
}
